/**
 * Engagement Tracking Utilities
 *
 * Easy-to-use functions for tracking user engagement throughout the app.
 * Import these functions and call them when users complete meaningful actions.
 */

import { engagementTrackerProvider } from "../AppController";

// ============================================================================
// WORKFLOW COMPLETIONS (High-value actions)
// ============================================================================

/**
 * Track when user marks a flag as reviewed
 * @param context - Additional context (flagId, patientId, severity, etc.)
 */
export const trackFlagReviewed = (context?: Record<string, any>) => {
  engagementTrackerProvider.trackWorkflowCompletion("flag_reviewed", context);
  // Dispatch event to trigger survey check
  window.dispatchEvent(new Event("workflowCompleted"));
};

/**
 * Track when user adds patient to watchlist
 * @param context - Additional context (patientId, reason, etc.)
 */
export const trackWatchlistAdd = (context?: Record<string, any>) => {
  engagementTrackerProvider.trackWorkflowCompletion("watchlist_add", context);
  window.dispatchEvent(new Event("workflowCompleted"));
};

/**
 * Track when user resolves/closes a clinical alert
 * @param context - Additional context (alertId, alertType, resolution, etc.)
 */
export const trackAlertResolved = (context?: Record<string, any>) => {
  engagementTrackerProvider.trackWorkflowCompletion("alert_resolved", context);
  window.dispatchEvent(new Event("workflowCompleted"));
};

/**
 * Track when user submits clinical notes/documentation
 * @param context - Additional context (noteType, patientId, etc.)
 */
export const trackNotesSubmitted = (context?: Record<string, any>) => {
  engagementTrackerProvider.trackWorkflowCompletion("notes_submitted", context);
  window.dispatchEvent(new Event("workflowCompleted"));
};

/**
 * Track when user acknowledges and resolves risk alerts
 * @param context - Additional context (alertId, riskLevel, action, etc.)
 */
export const trackRiskAlertResolved = (context?: Record<string, any>) => {
  engagementTrackerProvider.trackWorkflowCompletion(
    "risk_alert_resolved",
    context
  );
  window.dispatchEvent(new Event("workflowCompleted"));
};

/**
 * Track when user enables/disables bed exit alerts
 * @param context - Additional context (patientId, isEnabled, settingType, etc.)
 */
export const trackBedExitSettingChanged = (context?: Record<string, any>) => {
  engagementTrackerProvider.trackWorkflowCompletion(
    "bed_exit_setting_changed",
    context
  );
  window.dispatchEvent(new Event("workflowCompleted"));
};

/**
 * Track when user sets up bed exit notification (initial setup)
 * @param context - Additional context (patientId, settings, etc.)
 */
export const trackBedExitNotificationSetup = (
  context?: Record<string, any>
) => {
  engagementTrackerProvider.trackWorkflowCompletion(
    "bed_exit_notification_setup",
    context
  );
  window.dispatchEvent(new Event("workflowCompleted"));
};

/**
 * Track when consent is approved
 * @param context - Additional context (patientId, consentType, etc.)
 */
export const trackConsentApproved = (context?: Record<string, any>) => {
  engagementTrackerProvider.trackWorkflowCompletion(
    "consent_approved",
    context
  );
  window.dispatchEvent(new Event("workflowCompleted"));
};

/**
 * Track when consent is denied
 * @param context - Additional context (patientId, consentType, reason, etc.)
 */
export const trackConsentDenied = (context?: Record<string, any>) => {
  engagementTrackerProvider.trackWorkflowCompletion("consent_denied", context);
  window.dispatchEvent(new Event("workflowCompleted"));
};

/**
 * Track when user reports an issue in device offline tab
 * @param context - Additional context (deviceId, issueType, description, etc.)
 */
export const trackDeviceIssueReported = (context?: Record<string, any>) => {
  engagementTrackerProvider.trackWorkflowCompletion(
    "device_issue_reported",
    context
  );
  window.dispatchEvent(new Event("workflowCompleted"));
};

// ============================================================================
// POSITIVE EVENTS (Trigger moments - user found value)
// ============================================================================

/**
 * Track when user downloads a PDF (Morning Report, Patient Report, etc.)
 * @param context - Additional context (reportType, patientId, etc.)
 */
export const trackPDFDownload = (context?: Record<string, any>) => {
  engagementTrackerProvider.trackPositiveEvent("pdf_download", context);
  // Don't trigger immediately - wait a moment after download completes
  setTimeout(() => {
    window.dispatchEvent(new Event("positiveEvent"));
  }, 2000); // 2 second delay
};

/**
 * Track when user downloads CSV data
 * @param context - Additional context (dataType, dateRange, etc.)
 */
export const trackCSVDownload = (context?: Record<string, any>) => {
  engagementTrackerProvider.trackPositiveEvent("csv_download", context);
  setTimeout(() => {
    window.dispatchEvent(new Event("positiveEvent"));
  }, 2000);
};

/**
 * Track when Morning Report is successfully generated
 * @param context - Additional context (date, patientCount, etc.)
 */
export const trackMorningReportGenerated = (context?: Record<string, any>) => {
  engagementTrackerProvider.trackPositiveEvent(
    "morning_report_generated",
    context
  );
  setTimeout(() => {
    window.dispatchEvent(new Event("positiveEvent"));
  }, 1000);
};

/**
 * Track when user successfully completes onboarding
 */
export const trackOnboardingCompleted = () => {
  engagementTrackerProvider.trackPositiveEvent("onboarding_completed");
  window.dispatchEvent(new Event("positiveEvent"));
};

/**
 * Track when user opens "Create Summary" sidebar from Insights page
 * @param context - Additional context (tabName, insightType, etc.)
 */
export const trackInsightsSummaryOpened = (context?: Record<string, any>) => {
  engagementTrackerProvider.trackPositiveEvent(
    "insights_summary_opened",
    context
  );
  // Track as positive engagement (user interacting with insights)
  setTimeout(() => {
    window.dispatchEvent(new Event("positiveEvent"));
  }, 1000);
};

/**
 * Track when user downloads CSV from Insights page
 * @param context - Additional context (tabName, dataType, rowCount, etc.)
 */
export const trackInsightsCSVDownload = (context?: Record<string, any>) => {
  engagementTrackerProvider.trackPositiveEvent(
    "insights_csv_download",
    context
  );
  setTimeout(() => {
    window.dispatchEvent(new Event("positiveEvent"));
  }, 2000);
};

/**
 * Track when user applies filters to explore and analyze data
 * @param context - Additional context (filterType, filterValues, page, count, etc.)
 */
export const trackFilterApplied = (context?: Record<string, any>) => {
  engagementTrackerProvider.trackPositiveEvent("filter_applied", context);
  window.dispatchEvent(new Event("positiveEvent"));
};

// ============================================================================
// MILESTONES (Achievements worth celebrating)
// ============================================================================

/**
 * Track when user reviews N patients
 * @param count - Number of patients reviewed
 */
export const trackPatientsReviewed = (count: number) => {
  // Only fire milestone at meaningful thresholds
  const milestones = [5, 10, 25, 50, 100];
  if (milestones.includes(count)) {
    engagementTrackerProvider.trackMilestone("patients_reviewed", count, {
      threshold: count,
    });
    window.dispatchEvent(new Event("milestoneAchieved"));
  }
};

/**
 * Track consecutive days of usage
 * @param days - Number of consecutive days
 */
export const trackConsecutiveDaysUsed = (days: number) => {
  const milestones = [3, 5, 7, 14, 30];
  if (milestones.includes(days)) {
    engagementTrackerProvider.trackMilestone("consecutive_days", days, {
      threshold: days,
    });
    window.dispatchEvent(new Event("milestoneAchieved"));
  }
};

/**
 * Track total alerts resolved
 * @param count - Total number of alerts resolved
 */
export const trackAlertsResolved = (count: number) => {
  const milestones = [10, 25, 50, 100];
  if (milestones.includes(count)) {
    engagementTrackerProvider.trackMilestone("alerts_resolved_total", count, {
      threshold: count,
    });
    window.dispatchEvent(new Event("milestoneAchieved"));
  }
};

// ============================================================================
// PAGE VIEW TRACKING (Module context)
// ============================================================================

/**
 * Track page view with module context
 * @param moduleName - Name of the module (e.g., "Morning Report", "Dashboard", "Patient Profile")
 * @param route - Current route path
 */
export const trackPageView = (moduleName: string, route: string) => {
  engagementTrackerProvider.trackPageView(moduleName, route);
};

/**
 * Helper to extract module name from route
 * @param route - Route path (e.g., "/dashboard", "/morning-report")
 * @returns Friendly module name
 */
export const getModuleNameFromRoute = (route: string): string => {
  const routeMap: Record<string, string> = {
    "/dashboard": "Dashboard",
    "/morning-report": "Morning Report",
    "/patient-profile": "Patient Profile",
    "/insights-360": "Insights 360",
    "/alerts": "Clinical Alerts",
    "/watchlist": "Watchlist",
    "/residents": "Residents",
    "/settings": "Settings",
    "/vitals-ai": "VitalSync",
    "/billing-admin": "Billing Admin",
    "/billing": "Documentation",
    "/reports": "Reports",
  };

  // Find matching route
  for (const [path, name] of Object.entries(routeMap)) {
    if (route.startsWith(path)) {
      return name;
    }
  }

  return "Other";
};

// ============================================================================
// SESSION MANAGEMENT
// ============================================================================

/**
 * Start tracking a session (call on app load/login)
 */
export const startSession = () => {
  engagementTrackerProvider.startSession();
};

/**
 * End current session (call on logout/app close)
 */
export const endSession = () => {
  engagementTrackerProvider.endSession();
};

// ============================================================================
// DEBUGGING UTILITIES
// ============================================================================

/**
 * Get engagement summary for debugging
 */
export const getEngagementSummary = () => {
  return engagementTrackerProvider.getEngagementSummary();
};

/**
 * Check if user is eligible for survey (for debugging)
 */
export const checkSurveyEligibility = () => {
  return engagementTrackerProvider.isEligibleForSurvey();
};
